/**
 * @typedef {object} StatGroup - player stats or team stats object, ex: { totalYards: 10 }
 * @typedef {object} Play - current play
 * @typedef {string} StatName - exammples: "passingYards", "penalties"
 * @typedef {object} LimitedStats
 *    @property {string} player - name of stat for player - only this stat will be calculated
 *    @property {string} team - name of stat for team - only this stat will be calculated
 * @typedef {{ home: {teamStats: object, playerStats: array}, away: {teamStats: object, playerStats: array} }} FullStatsObject
 * @typedef {("home"|"away")} Side
 */

import _ from "lodash";
import { isDisplayable } from "util/misc";
import {
  getPreviousPlay,
  getTeamSideFromId,
  getOppositeSide,
  filterStatsByAllowedKeys
} from "games/utils";
import {
  setNull,
  setNullTeamStatsIfAllowed,
  setNullTeamStats
} from "../actions";
import {
  getStatsFromTotals,
  getTeamStatsFromTotals
} from "./postStatsFromTotals";
import statsByPlayType from "./statsByPlayType.json";
import getChanges from "./getChanges";

/*
 * @param {Side} side
 * @param {FullStatsObject} previousStats - stats from latest version of state, before they were re-calculated from plays
 * @param {StatGroup} statsFromPlays - stats calculated from most recent plays
 * @param {LimitedStats} limitedStats
 * @param {boolean} shouldClearOldStats
 * @returns {object}
 */
const getTransformedPlayerStats = ({
  side,
  previousStats,
  statsFromPlays,
  limitedStats = null,
  shouldClearOldStats = true
}) => {
  const previousPlayerStats = _.get(previousStats, [side, "playerStats"]);
  const playerLimitedStats = _.get(limitedStats, "player", []);

  return previousPlayerStats.map((previousPlayerStats) => {
    const { id } = previousPlayerStats;
    const playerStatsFromPlays = _.get(statsFromPlays, id, {}) || {};

    let statsBase = {};
    // Sets the existing player stats to null. For example, if a player only has 1 stat (let's say "totalPoints"), only that 1 stat will get set to null.
    // All other player stats (like for example "rushingYards", "passingYards", etc) will be ignored because they haven't previously been set.
    // We want this functionality so that we aren't nulling-out stats unnessisarily.
    if (shouldClearOldStats) {
      statsBase = setNull(previousPlayerStats, ["id", "name"]);
      // Keep all stats from previous state without setting them to null, EXCEPT for any explicity listed stats ("limited stats").
      // null-out any limited stats -- this may get overwritten by a calculated value, but needs to be set to null here
      // in case the stat was actually deleted
    } else if (isDisplayable(limitedStats)) {
      statsBase = setNull(previousPlayerStats, [
        "id",
        "name",
        ...playerLimitedStats
      ]);
      // Keep all stats from previous state without setting them to null.
    } else {
      statsBase = previousPlayerStats;
    }

    // only the new calculated stats with truthy or 0 values.
    // If a stat was set to null, that is handled in the statsBase insetad of here
    const unfilteredPlayerStats = {
      ...playerStatsFromPlays,
      ...getStatsFromTotals(playerStatsFromPlays)
    };

    const filteredPlayerStats = isDisplayable(limitedStats)
      ? filterStatsByAllowedKeys(unfilteredPlayerStats, playerLimitedStats)
      : unfilteredPlayerStats;

    return {
      ...statsBase,
      ...filteredPlayerStats
    };
  });
};

/*
 * @param {Side} side
 * @param {object} previousPlay
 * @param {FullStatsObject} previousStats
 * @param {StatGroup} statsFromPlays
 * @param {LimitedStats} limitedStats
 * @param {boolean} shouldClearOldStats
 * @returns {object}
 */
const getTransformedTeamStats = ({
  side,
  previousPlay,
  previousStats,
  statsFromPlays,
  limitedStats = null,
  shouldClearOldStats = true
}) => {
  const sideTeamStatsFromPlays = _.get(statsFromPlays, `${side}Team`);
  const oppositeSideTeamStatsFromPlays = _.get(
    statsFromPlays,
    `${getOppositeSide(side)}Team`
  );
  const teamStatsFromTotals = getTeamStatsFromTotals({
    previousPlay,
    side,
    sideTeamStats: sideTeamStatsFromPlays,
    oppositeSideTeamStats: oppositeSideTeamStatsFromPlays
  });
  const previousTeamStats = _.get(previousStats, [side, "teamStats"]);
  const teamLimitedStats = _.get(limitedStats, "team", []);

  let statsBase = {};
  // Sets the existing team stats to null. For example, if a team only has 1 stat (let's say "totalPoints"), only that 1 stat will get set to null.
  // All other team stats (like for example "rushingYards", "passingYards", etc) will be ignored because they haven't previously been set. We want this
  // functionality so that we aren't nulling-out stats unnessisarily.
  if (shouldClearOldStats) {
    statsBase = setNullTeamStats({ stats: previousStats, side });
    // Keep all stats from previous state without setting them to null, EXCEPT for any limited stats.
    // null-out any limited stats -- this may get overwritten by a calulated value, but needs to be set to null here
    // in case the stat was actually deleted
  } else if (isDisplayable(limitedStats)) {
    statsBase = setNullTeamStatsIfAllowed({
      stats: previousStats,
      side,
      allowed: teamLimitedStats
    });
    // Keep all stats from previous state without setting them to null.
  } else {
    statsBase = previousTeamStats;
  }

  // only the new calculated stats with truthy or 0 values.
  // If a stat was set to null, that is handled in the statsBase insetad of here
  const unfilteredStats = {
    ...sideTeamStatsFromPlays,
    ...teamStatsFromTotals,
    ...getStatsFromTotals(sideTeamStatsFromPlays)
  };

  const filteredStats = isDisplayable(limitedStats)
    ? filterStatsByAllowedKeys(unfilteredStats, teamLimitedStats)
    : unfilteredStats;

  return {
    ...statsBase,
    ...filteredStats
  };
};

/*
 * Calculate stats from most recent set of plays
 * @param {array} plays
 * @param {object} game
 * @returns {object}
 */
const getStatsFromPlays = (plays, game) => {
  return plays.reduce((acc, play, i, arr) => {
    const {
      player1Id = null,
      player2Id = null,
      playType = null,
      safetyType = null
    } = play;

    const statType = safetyType || playType;
    const player1Stats = _.get(acc, player1Id, {});
    const player2Stats = _.get(acc, player2Id, {});
    const homeTeamStats = _.get(acc, "homeTeam", {});
    const awayTeamStats = _.get(acc, "awayTeam", {});

    const { teamInPossessionId } = play;
    const offenseTeamSide = getTeamSideFromId(game, teamInPossessionId);
    const isOffense = (side) => offenseTeamSide === side;

    const getTeamFieldNames = (side) => {
      return isOffense(side)
        ? _.get(statsByPlayType, [statType, "teamOffense"])
        : _.get(statsByPlayType, [statType, "teamDefense"]);
    };

    const getTeamType = (side) => {
      return isOffense(side) ? "teamOffense" : "teamDefense";
    };

    const player1FieldNames = _.get(statsByPlayType, [statType, "player1"]);
    const player2FieldNames = _.get(statsByPlayType, [statType, "player2"]);
    const homeTeamFieldNames = getTeamFieldNames("home");
    const awayTeamFieldNames = getTeamFieldNames("away");

    const transformedPlayer1 = isDisplayable(player1Id)
      ? {
          ...player1Stats,
          ...getChanges({
            fieldNames: player1FieldNames,
            item: player1Stats,
            type: "player1",
            play,
            game
          })
        }
      : null;

    const transformedPlayer2 = isDisplayable(player2Id)
      ? {
          ...player2Stats,
          ...getChanges({
            fieldNames: player2FieldNames,
            item: player2Stats,
            type: "player2",
            play,
            game
          })
        }
      : null;

    const transformedHomeTeam = {
      ...homeTeamStats,
      ...getChanges({
        fieldNames: homeTeamFieldNames,
        item: homeTeamStats,
        type: getTeamType("home"),
        side: "home",
        play,
        game
      })
    };

    const transformedAwayTeam = {
      ...awayTeamStats,
      ...getChanges({
        fieldNames: awayTeamFieldNames,
        item: awayTeamStats,
        type: getTeamType("away"),
        side: "away",
        play,
        game
      })
    };

    const changes = {
      ...acc,
      ...(transformedPlayer1 && {
        [player1Id]: transformedPlayer1
      }),
      ...(transformedPlayer2 && {
        [player2Id]: transformedPlayer2
      }),
      homeTeam: transformedHomeTeam,
      awayTeam: transformedAwayTeam
    };

    return changes;
  }, {});
};

/*
 * Calculate all stats
 * @param {object} plays
 * @param {object} state
 * @param {boolean} forceOverride - re-calculate stats even if the status is set to final
 * @param {LimitedStats} limitedStats
 * @param {boolean} shouldClearOldStats - should only be set to false if you want to re-calculate some stats but not others via the limited stats param
 * @returns {FullStatsObject}
 */
const postStatsChanges = ({
  plays,
  state,
  forceOverride = false,
  limitedStats = null,
  shouldClearOldStats = true
}) => {
  const { stats, game, gameStatus } = state;
  // don't want to overwrite stats if game status is final
  if (gameStatus === "FINAL" && !forceOverride) {
    return stats;
  }
  const statsFromPlays = getStatsFromPlays(plays, game);
  const previousPlay = getPreviousPlay(plays);

  const playerStatsParams = {
    previousStats: stats,
    statsFromPlays,
    limitedStats,
    shouldClearOldStats
  };
  const teamStatsParams = {
    previousPlay,
    statsFromPlays,
    previousStats: stats,
    limitedStats,
    shouldClearOldStats
  };
  const transformedHomePlayerStats = getTransformedPlayerStats({
    side: "home",
    ...playerStatsParams
  });
  const transformedAwayPlayerStats = getTransformedPlayerStats({
    side: "away",
    ...playerStatsParams
  });
  const transformedHomeTeamStats = getTransformedTeamStats({
    side: "home",
    ...teamStatsParams
  });
  const transformedAwayTeamStats = getTransformedTeamStats({
    side: "away",
    ...teamStatsParams
  });

  const outputStats = {
    home: {
      teamStats: transformedHomeTeamStats,
      playerStats: transformedHomePlayerStats
    },
    away: {
      teamStats: transformedAwayTeamStats,
      playerStats: transformedAwayPlayerStats
    }
  };

  return outputStats;
};

export { postStatsChanges };
